'use client'
import { yupResolver } from '@hookform/resolvers/yup'
import { LinePhone } from '@qr-front/icons/line-phone'
import { GENERATOR_FORM_ANIMATION_DURATION } from 'features/generator/components/GeneratorFormSection'
import { RhcField } from 'features/generator/components/RhcField'
import { qrTypeAtom } from 'features/generator/generatorState'
import { QR_TYPE } from 'features/generator/generatorTypes'
import { useAtomValue } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, useImperativeHandle, useRef } from 'react'
import { useBus } from 'react-bus'
import { FormProvider, Resolver, useForm } from 'react-hook-form'
import {
	scrollToErrorWithAccordion,
	validateContentForm,
} from 'utils/formUtils'

import { transformBackendResponseKey } from '@/config'
import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'
import { maxLength } from '@/utils'

import { OPEN_CONTENT_ACCORDIONS_EVENT } from '../../contentFormConstants'
import {
	contactInfoEmailListAtom,
	contactInfoPhoneListAtom,
	contactInfoWebsiteAtom,
	ContactSection,
} from '../CommonSections/ContactSection'
import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { useSyncFormSectionValidity } from '../useSyncFormSectionValidity'
import {
	getContactInfoSchema,
	TContactInfoFormFields,
} from './contactInfoFormModel'

export const CONTACT_INFO_FORM_ID = 'contact-info'

export const contactInfoFullNameAtom = atomWithReset<string | undefined>('')

export const resetContactInfoForm = () => {
	getStore().set(contactInfoFullNameAtom, RESET)
	getStore().set(contactInfoWebsiteAtom, RESET)
	getStore().set(contactInfoPhoneListAtom, RESET)
	getStore().set(contactInfoEmailListAtom, RESET)
}

export const ContactInfoForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()
	const qrType = useAtomValue(qrTypeAtom)
	const isBusinessPageQrType = QR_TYPE.BusinessPage === qrType
	const formRef = useRef<HTMLFormElement>(null)
	const bus = useBus()

	const formDefaultValues: TContactInfoFormFields = {
		personal: {
			fullName: getStore().get(contactInfoFullNameAtom),
		},
		contacts: {
			phone: getStore()
				.get(contactInfoPhoneListAtom)
				?.map((entry) => ({
					value: entry.value,
				})),
			website: getStore().get(contactInfoWebsiteAtom),
			email: getStore()
				.get(contactInfoEmailListAtom)
				?.map((entry) => ({
					value: entry.value,
				})),
		},
	}

	const form = useForm<TContactInfoFormFields>({
		defaultValues: formDefaultValues,
		mode: 'onTouched',
		resolver: yupResolver(
			getContactInfoSchema(t),
		) as unknown as Resolver<TContactInfoFormFields>,
		reValidateMode: 'onChange',
		shouldUnregister: false,
	})

	const {
		formState: { isValid, errors },
		handleSubmit,
	} = form

	useSyncFormSectionValidity({ formId: CONTACT_INFO_FORM_ID, isValid })

	useImperativeHandle(
		ref,
		() => ({
			trigger: (property: string, message: string) => {
				const translatedMessage = t(
					transformBackendResponseKey(message),
				)
				let mappedProperty = property
				if (property === 'contactDetails.name') {
					mappedProperty = 'personal.fullName'
				} else if (property.startsWith('contacts.email')) {
					const emailIndexMatch = property.match(
						/contacts\.email(?:\[(\d+)\]|\.(\d+))/,
					)
					if (emailIndexMatch) {
						const index = emailIndexMatch[1]
						mappedProperty = `contacts.email.${index}.value`
					} else if (property === 'contacts.email') {
						mappedProperty = 'contacts.email.0.value'
					}
				} else {
					mappedProperty = property
				}
				form.setError(
					mappedProperty as Parameters<typeof form.setError>[0],
					{
						message: translatedMessage,
					},
				)

				scrollToErrorWithAccordion({
					form,
					bus,
					formId: CONTACT_INFO_FORM_ID,
					formRef,
					animationDuration: GENERATOR_FORM_ANIMATION_DURATION * 1.25,
					openAccordionEvent: OPEN_CONTENT_ACCORDIONS_EVENT,
				})
			},
			validate: () =>
				validateContentForm(
					(onValid, onInvalid) => handleSubmit(onValid, onInvalid),
					GENERATOR_FORM_ANIMATION_DURATION * 1.25,
				),
		}),
		[handleSubmit, errors],
	)

	return (
		<form ref={formRef} className="flex flex-col gap-4">
			<FormProvider {...form}>
				<RhcField
					type="text"
					form={form}
					path="personal.fullName"
					atom={contactInfoFullNameAtom}
					fieldName="full_name"
					label={t(
						'generator__content_form_section__about__personal_section__full_name',
					)}
					placeholder={
						isBusinessPageQrType
							? t(
									'generator__content_form_section__contact_info_section__full_name__placeholder',
								)
							: t(
									'generator__content_form_section__about__personal_section__full_name__placeholder',
								)
					}
					onChangeTransform={(event) => maxLength(301)(event)}
					className="col-span-2 tablet:col-span-1"
				/>
				<ContactSection
					phonePath="contacts.phone"
					websitePath="contacts.website"
					emailPath="contacts.email"
					websiteAtom={contactInfoWebsiteAtom}
				/>
			</FormProvider>
		</form>
	)
})

ContactInfoForm.displayName = 'ContactInfoForm'

export const contactInfoFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: CONTACT_INFO_FORM_ID,
	title: t('generator__content_form_section__contact_info__title'),
	description: t(
		'generator__content_form_section__contact_info__description',
	),
	icon: <LinePhone />,
	Content: ContactInfoForm,
})
